import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ContactSupport as ContactSupportIcon
} from '@mui/icons-material';

const PrivacyPolicyPage = ({ onBack }) => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Stack spacing={4}>
          {/* Header */}
          <Box>
            {onBack && (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
                variant="outlined"
                sx={{ mb: 2 }}
              >
                Back
              </Button>
            )}
            <Typography variant="h3" component="h1" gutterBottom>
              Privacy Policy
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Last updated: February 8, 2025
            </Typography>
          </Box>

          <Divider />

          {/* Introduction */}
          <Box>
            <Typography variant="h5" gutterBottom>
              1. Introduction
            </Typography>
            <Typography variant="body1" paragraph>
              Welcome to Life Runbooks ("we," "our," or "us"). We are committed to protecting your privacy 
              and ensuring the security of your personal information. This Privacy Policy explains how we 
              collect, use, disclose, and safeguard your information when you use our Life Runbooks 
              application and services.
            </Typography>
            <Typography variant="body1">
              By using our service, you agree to the collection and use of information in accordance with 
              this Privacy Policy. If you do not agree with our policies and practices, do not use our service.
            </Typography>
          </Box>

          {/* Information We Collect */}
          <Box>
            <Typography variant="h5" gutterBottom>
              2. Information We Collect
            </Typography>
            
            <Typography variant="h6" gutterBottom>
              2.1 Personal Information
            </Typography>
            <Typography variant="body1" paragraph>
              We collect information you provide directly to us, including:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Account Information"
                  secondary="Name, email address, profile information"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Authentication Data"
                  secondary="Login credentials, OAuth tokens from third-party providers"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Family Information"
                  secondary="Family member details, roles, and permissions"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Runbook Content"
                  secondary="Emergency procedures, steps, descriptions, and related data"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Emergency Contacts"
                  secondary="Names and phone numbers of emergency contacts"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Communication Data"
                  secondary="Messages, support requests, and feedback"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              2.2 Automatically Collected Information
            </Typography>
            <Typography variant="body1" paragraph>
              When you use our service, we automatically collect:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Usage Data"
                  secondary="Pages visited, features used, time spent, click patterns"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Device Information"
                  secondary="Device type, operating system, browser type and version"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Log Data"
                  secondary="IP address, access times, error logs, performance data"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Analytics Data"
                  secondary="User interactions, session duration, conversion events (with consent)"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Location Data"
                  secondary="General location based on IP address (not precise location)"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              2.3 Third-Party Information
            </Typography>
            <Typography variant="body1" paragraph>
              We may receive information from third-party services:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Social Media"
                  secondary="Profile information from Google, Facebook, GitHub when you connect accounts"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Analytics Services"
                  secondary="Google Analytics data (with your consent)"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Payment Processors"
                  secondary="Transaction data (if applicable to future paid features)"
                />
              </ListItem>
            </List>
          </Box>

          {/* How We Use Information */}
          <Box>
            <Typography variant="h5" gutterBottom>
              3. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use the collected information for the following purposes:
            </Typography>
            
            <Typography variant="h6" gutterBottom>
              3.1 Service Provision
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Create and manage your account"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Provide access to runbooks and family management features"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Enable emergency contact functionality"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Facilitate family member invitations and collaboration"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Synchronize data across your devices"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              3.2 Service Improvement
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Analyze usage patterns to improve user experience"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Develop new features and functionality"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Optimize application performance and reliability"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Conduct research and analytics (with consent)"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              3.3 Communication
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Send important service notifications and updates"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Respond to your inquiries and support requests"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Send security alerts and account notifications"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Provide customer support and technical assistance"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              3.4 Legal and Security
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Comply with legal obligations and regulations"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Protect against fraud, abuse, and security threats"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Enforce our Terms of Service and other policies"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Resolve disputes and investigate violations"
                />
              </ListItem>
            </List>
          </Box>

          {/* Contact Information */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ContactSupportIcon color="primary" />
              <Typography variant="body1">
                Email: privacy@liferunbooks.com
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              We will respond to your inquiry within 30 days.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicyPage;